import { ReferenceContactId, RegionId } from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { ContentContainer, PageHeader, Stage } from "components/layout";
import { MessageCompose } from "components/messages/message-compose";
import { trpc } from "components/rpc/trpc";
import { Spinner } from "components/ui/spinner";
import { AdvancedSearchEntityType } from "generated/graphql";
import React, { useState } from "react";
import { Redirect } from "react-router";
import { useFeature, useRegionId } from "store/selectors/hooks";
import { usePhoneLines } from "store/selectors/telecom";
import { handleEvent } from "util/user-events";
import { AgendaProvisionBanner } from "./agenda-actions";
import { AgendaContactCard } from "./agenda-contact-card";
import {
    agendaNeedsPhoneProvisioning,
    groupDueTouchpoints,
} from "./marketing-plan-agenda";

export const MarketingPlanPage: React.FC<unknown> = () => {
    const marketingPlaybookEnabled = useFeature("marketingPlaybook");
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const phoneLines = usePhoneLines();
    const [composingContactId, setComposingContactId] =
        useState<ReferenceContactId | null>(null);

    const dueQuery = useQuery({
        ...trpcClient.marketingPlaybook.dueTouchpoints.queryOptions({
            regionId: regionId ?? ("" as RegionId),
        }),
        enabled: marketingPlaybookEnabled && !!regionId,
    });

    if (!marketingPlaybookEnabled) {
        return <Redirect to="/tasks" />;
    }

    const hasPhoneLine = phoneLines.length > 0;
    const groups = groupDueTouchpoints(dueQuery.data ?? []);
    const showProvisionBanner =
        !dueQuery.isLoading && agendaNeedsPhoneProvisioning(groups, hasPhoneLine);

    const openCompose = (contactId: string) => {
        handleEvent("marketing-plan-send-email");
        setComposingContactId(contactId as ReferenceContactId);
    };

    const closeCompose = () => {
        setComposingContactId(null);
        dueQuery.refetch();
    };

    return (
        <Stage>
            <PageHeader title="My Agenda" />
            <ContentContainer>
                <div className="space-y-4 px-3 py-3 sm:px-0 lg:py-4">
                    <p className="text-sm text-gray-500">
                        Referral sources due for outreach, based on the touchpoint
                        cadences set for each tier.
                    </p>
                    {showProvisionBanner ? <AgendaProvisionBanner /> : null}
                    {dueQuery.isLoading ? (
                        <div className="flex justify-center rounded-lg bg-white p-12 shadow">
                            <Spinner />
                        </div>
                    ) : groups.length === 0 ? (
                        <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-500 shadow">
                            All caught up — no referral sources are due for outreach right
                            now.
                        </div>
                    ) : (
                        <ul className="space-y-3 sm:space-y-4">
                            {groups.map(group => (
                                <li key={group.contactId}>
                                    <AgendaContactCard
                                        group={group}
                                        hasPhoneLine={hasPhoneLine}
                                        onSendEmail={openCompose}
                                    />
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </ContentContainer>
            {composingContactId ? (
                <MessageCompose
                    entityId={composingContactId}
                    entityType={AdvancedSearchEntityType.ReferenceContact}
                    onDismiss={closeCompose}
                />
            ) : null}
        </Stage>
    );
};
